import { useCallback, useEffect, useState } from "react";

import { API_BASE_URL } from "../../config";
import { useAuth } from "../../context/AuthContext";

const EMAIL_RESEND_DEFAULT = parseInt(process.env.REACT_APP_EMAIL_RESEND_COOLDOWN || "60", 10);

export const useEmailVerificationReminder = () => {
  const { authFetch, isAuthenticated, isEmailVerified } = useAuth();
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let ignore = false;
    if (!isAuthenticated || isEmailVerified) {
      setCooldown(0);
      return () => {
        ignore = true;
      };
    }

    (async () => {
      try {
        const resp = await authFetch(`${API_BASE_URL}/api/user-status/`);
        const data = await resp.json().catch(() => ({}));
        if (!ignore) {
          setCooldown(data.verification_cooldown_seconds || 0);
        }
      } catch {
        if (!ignore) {
          setCooldown(0);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [authFetch, isAuthenticated, isEmailVerified]);

  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((value) => (value > 0 ? value - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const resendVerification = useCallback(async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    try {
      const resp = await authFetch(`${API_BASE_URL}/api/resend-verification/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await resp.json().catch(() => ({}));
      const nextCooldown =
        typeof data.cooldown_seconds === "number" ? data.cooldown_seconds : EMAIL_RESEND_DEFAULT;

      if (resp.ok || resp.status === 429) {
        setCooldown(nextCooldown);
      } else {
        throw new Error(data.detail || data.message || "Failed to resend verification email.");
      }
    } catch (error) {
      console.error("Failed to resend verification email:", error);
    } finally {
      setResending(false);
    }
  }, [authFetch, cooldown, resending]);

  return {
    cooldown,
    resending,
    resendVerification,
  };
};
